// frontend/src/utils/analytics.js

/**
 * Google Analytics 4 Tracking Utility
 * Centralizza tutti gli eventi di tracking per BetterPDP
 */

// Verifica se GA4 è disponibile
const isGAAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// 🔥 FIX: Check consent - only "accepted" is saved, decline is not persistent
const hasConsent = () => {
  try {
    const consentData = localStorage.getItem('betterpdp_consent');
    
    if (!consentData) {
      return false; // Nessun consenso dato
    }

    // Prova a parsare come JSON (nuovo formato)
    try {
      const { status, timestamp } = JSON.parse(consentData);
      const now = Date.now();
      const oneYear = 365 * 24 * 60 * 60 * 1000;
      
      // Consenso valido solo se:
      // 1. Status è 'accepted' (decline non viene salvato)
      // 2. Non è scaduto (< 1 anno)
      if (status === 'accepted' && (now - timestamp) < oneYear) {
        return true;
      }
      
      return false;
    } catch (e) {
      // Formato vecchio (solo stringa 'accepted')
      return consentData === 'accepted';
    }
  } catch (e) {
    console.error('Error checking consent:', e);
    return false;
  }
};

// Inizializza GA4 (opzionale, già fatto in index.html)
export const initGA = () => {
  if (isGAAvailable()) {
    console.log('✅ GA4 available');
    return true;
  }
  console.warn('⚠️ GA4 not loaded - events will not be tracked');
  return false;
};

// Track page view manualmente (per React Router)
export const trackPageView = (path, title) => {
  if (!isGAAvailable()) return;
  
  // 🔥 Check consent
  if (!hasConsent()) {
    console.log('⏭️ Pageview not tracked (no consent)');
    return;
  }
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
  
  console.log('📊 Pageview tracked:', path);
};

// Funzione generica per tracciare eventi
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isGAAvailable()) return;
  
  // 🔥 Check consent
  if (!hasConsent()) {
    console.log(`⏭️ Event "${eventName}" not tracked (no consent)`);
    return;
  }
  
  window.gtag('event', eventName, eventParams);
  console.log(`📊 Event tracked: ${eventName}`, eventParams);
};

/**
 * EVENTI SPECIFICI BETTERPDP
 * Organizzati per sezione/funzionalità
 */

export const GAEvents = {
  // ============================================
  // HERO SECTION
  // ============================================
  
  heroUrlPaste: (url) => {
    trackEvent('hero_url_paste', {
      event_category: 'Engagement',
      event_label: 'Hero URL Input',
      url_length: url ? url.length : 0,
      has_protocol: url ? url.startsWith('http') : false,
    });
  },

  heroCtaClick: (hasUrl, isLoggedIn = false) => {
    trackEvent('hero_cta_click', {
      event_category: 'Conversion',
      event_label: 'Get AI Score CTA',
      has_url: hasUrl,
      user_status: isLoggedIn ? 'logged_in' : 'guest',
    });
  },

  heroSecondaryCtaClick: (ctaLabel) => {
    trackEvent('hero_secondary_cta', {
      event_category: 'Engagement',
      event_label: ctaLabel,
    });
  },

  // ============================================
  // ANALYSIS FLOW
  // ============================================
  
  freeAnalysisStart: (url) => {
    trackEvent('free_analysis_start', {
      event_category: 'Engagement',
      event_label: 'Free Analysis Started',
      url: url,
      timestamp: new Date().toISOString(),
    });
  },

  freeAnalysisComplete: (score, url, duration) => {
    trackEvent('free_analysis_complete', {
      event_category: 'Conversion',
      event_label: 'Free Analysis Completed',
      score: score,
      url: url,
      duration_seconds: duration,
      score_category: score >= 80 ? 'excellent' : score >= 60 ? 'good' : score >= 40 ? 'fair' : 'poor',
    });
  },

  analysisError: (url, errorType, errorMessage) => {
    trackEvent('analysis_error', {
      event_category: 'Error',
      event_label: 'Analysis Failed',
      url: url,
      error_type: errorType,
      error_message: errorMessage,
    });
  },

  upgradeModalShown: (trigger) => {
    trackEvent('upgrade_modal_shown', {
      event_category: 'Conversion',
      event_label: 'Paywall Modal Shown',
      trigger: trigger,
    });
  },

  upgradeModalClosed: (action) => {
    trackEvent('upgrade_modal_closed', {
      event_category: 'Engagement',
      event_label: 'Paywall Modal Closed',
      action: action,
    });
  },

  // ============================================
  // AUTHENTICATION
  // ============================================
  
  signupInitiated: (method = 'email') => {
    trackEvent('signup_initiated', {
      event_category: 'User',
      event_label: 'Signup Started',
      method: method,
    });
  },

  signupCompleted: (method = 'email', userId = null) => {
    trackEvent('signup_completed', {
      event_category: 'User',
      event_label: 'Signup Completed',
      method: method,
      user_id: userId,
    });
  },

  signupError: (method, errorType) => {
    trackEvent('signup_error', {
      event_category: 'Error',
      event_label: 'Signup Failed',
      method: method,
      error_type: errorType,
    });
  },

  loginCompleted: (method = 'email', userId = null) => {
    trackEvent('login_completed', {
      event_category: 'User',
      event_label: 'Login Completed',
      method: method,
      user_id: userId,
    });
  },

  loginError: (method, errorType) => {
    trackEvent('login_error', {
      event_category: 'Error',
      event_label: 'Login Failed',
      method: method,
      error_type: errorType,
    });
  },

  logout: (userId = null) => {
    trackEvent('logout', {
      event_category: 'User',
      event_label: 'User Logged Out',
      user_id: userId,
    });
  },

  // ============================================
  // PRICING & CHECKOUT
  // ============================================
  
  pricingPageViewed: (source) => {
    trackEvent('pricing_page_viewed', {
      event_category: 'Engagement',
      event_label: 'Pricing Page Viewed',
      source: source,
    });
  },

  pricingTierSelected: (tier, price, credits) => {
    trackEvent('pricing_tier_selected', {
      event_category: 'Conversion',
      event_label: 'Pricing Tier Selected',
      tier: tier,
      price: parseFloat(price),
      credits: credits,
      value: parseFloat(price),
    });
  },

  checkoutInitiated: (tier, price, credits) => {
    trackEvent('begin_checkout', {
      event_category: 'Ecommerce',
      event_label: 'Checkout Started',
      tier: tier,
      value: parseFloat(price),
      currency: 'USD',
      items: [{
        item_id: tier,
        item_name: `BetterPDP ${tier}`,
        item_category: 'Credits',
        price: parseFloat(price),
        quantity: 1,
      }],
    });
  },

  // ============================================
  // PAYMENTS
  // ============================================
  
  paymentSuccess: (tier, price, credits, orderId) => {
    trackEvent('purchase', {
      event_category: 'Ecommerce',
      event_label: 'Payment Success',
      transaction_id: orderId,
      value: parseFloat(price),
      currency: 'USD',
      tax: 0,
      shipping: 0,
      items: [{
        item_id: tier,
        item_name: `BetterPDP ${tier}`,
        item_category: 'Credits',
        price: parseFloat(price),
        quantity: 1,
      }],
    });
  },

  paymentFailed: (tier, price, errorType) => {
    trackEvent('payment_failed', {
      event_category: 'Ecommerce',
      event_label: 'Payment Failed',
      tier: tier,
      price: parseFloat(price),
      error_type: errorType,
    });
  },

  paymentCancelled: (tier, price) => {
    trackEvent('payment_cancelled', {
      event_category: 'Ecommerce',
      event_label: 'Payment Cancelled',
      tier: tier,
      price: parseFloat(price),
    });
  },

  // ============================================
  // DASHBOARD & ANALYSIS (Logged Users)
  // ============================================
  
  dashboardViewed: (creditsRemaining, totalAnalyses) => {
    trackEvent('dashboard_viewed', {
      event_category: 'Engagement',
      event_label: 'Dashboard Viewed',
      credits_remaining: creditsRemaining,
      total_analyses: totalAnalyses,
      has_lifetime: creditsRemaining === 999999,
    });
  },

  analysisRun: (creditsRemaining, isLifetime = false) => {
    trackEvent('analysis_run', {
      event_category: 'Engagement',
      event_label: 'PDP Analysis Run (Logged)',
      credits_remaining: creditsRemaining,
      is_lifetime: isLifetime,
    });
  },

  creditLowWarning: (creditsRemaining) => {
    trackEvent('credit_low_warning', {
      event_category: 'Engagement',
      event_label: 'Low Credits Warning Shown',
      credits_remaining: creditsRemaining,
    });
  },

  pdpDetailViewed: (pdpId, score) => {
    trackEvent('pdp_detail_viewed', {
      event_category: 'Engagement',
      event_label: 'PDP Detail Page Viewed',
      pdp_id: pdpId,
      score: score,
    });
  },

  // ============================================
  // FEATURES (Future)
  // ============================================
  
  pdfExportClicked: (pdpId) => {
    trackEvent('pdf_export_clicked', {
      event_category: 'Feature',
      event_label: 'PDF Export Clicked',
      pdp_id: pdpId,
    });
  },

  emailReportClicked: (pdpId) => {
    trackEvent('email_report_clicked', {
      event_category: 'Feature',
      event_label: 'Email Report Clicked',
      pdp_id: pdpId,
    });
  },

  bulkUploadInitiated: (fileCount) => {
    trackEvent('bulk_upload_initiated', {
      event_category: 'Feature',
      event_label: 'Bulk Upload Started',
      file_count: fileCount,
    });
  },

  // ============================================
  // ENGAGEMENT & CONTENT
  // ============================================
  
  videoPlay: (location, videoName) => {
    trackEvent('video_play', {
      event_category: 'Engagement',
      event_label: 'Tutorial Video Played',
      location: location,
      video_name: videoName,
    });
  },

  videoPause: (location, videoName, progress) => {
    trackEvent('video_pause', {
      event_category: 'Engagement',
      event_label: 'Tutorial Video Paused',
      location: location,
      video_name: videoName,
      progress_percentage: progress,
    });
  },

  videoComplete: (location, videoName) => {
    trackEvent('video_complete', {
      event_category: 'Engagement',
      event_label: 'Tutorial Video Completed',
      location: location,
      video_name: videoName,
    });
  },

  faqExpanded: (question, location) => {
    trackEvent('faq_expanded', {
      event_category: 'Engagement',
      event_label: 'FAQ Item Opened',
      question: question.substring(0, 100),
      location: location,
    });
  },

  testimonialClicked: (testimonialIndex) => {
    trackEvent('testimonial_clicked', {
      event_category: 'Engagement',
      event_label: 'Testimonial Clicked',
      testimonial_index: testimonialIndex,
    });
  },

  liveDemoRun: (demoUrl) => {
    trackEvent('live_demo_run', {
      event_category: 'Engagement',
      event_label: 'Live Demo Executed',
      demo_url: demoUrl,
    });
  },

  featureTabClicked: (tabName) => {
    trackEvent('feature_tab_clicked', {
      event_category: 'Engagement',
      event_label: 'Features Tab Clicked',
      tab_name: tabName,
    });
  },

  // ============================================
  // NAVIGATION & OUTBOUND
  // ============================================
  
  outboundClick: (url, label, location) => {
    trackEvent('outbound_click', {
      event_category: 'Navigation',
      event_label: label,
      url: url,
      location: location,
    });
  },

  ctaClick: (ctaLabel, location, destination) => {
    trackEvent('cta_click', {
      event_category: 'Conversion',
      event_label: ctaLabel,
      location: location,
      destination: destination,
    });
  },

  languageChanged: (fromLang, toLang) => {
    trackEvent('language_changed', {
      event_category: 'Engagement',
      event_label: 'Language Switched',
      from_language: fromLang,
      to_language: toLang,
    });
  },

  // ============================================
  // SCROLL DEPTH (Optional but useful)
  // ============================================
  
  scrollDepth: (percentage, page) => {
    if ([25, 50, 75, 100].includes(percentage)) {
      trackEvent('scroll_depth', {
        event_category: 'Engagement',
        event_label: `Scrolled ${percentage}%`,
        page: page,
        percentage: percentage,
      });
    }
  },

  // ============================================
  // ERRORS & ISSUES
  // ============================================
  
  errorOccurred: (errorType, errorMessage, location) => {
    trackEvent('error_occurred', {
      event_category: 'Error',
      event_label: errorType,
      error_message: errorMessage.substring(0, 150),
      location: location,
    });
  },

  // ============================================
  // SOCIAL & REFERRAL (Future)
  // ============================================
  
  socialShareClick: (platform, content) => {
    trackEvent('social_share_click', {
      event_category: 'Social',
      event_label: `Shared on ${platform}`,
      platform: platform,
      content_type: content,
    });
  },

  referralLinkCopied: (userId) => {
    trackEvent('referral_link_copied', {
      event_category: 'Referral',
      event_label: 'Referral Link Copied',
      user_id: userId,
    });
  },
};

// Export per uso singolo
export default GAEvents;